<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useWorkspaceSettingsStore } from '@/modules/workspace/stores/workspace-settings-store'
import WorkspaceSettingsLayout from '@/modules/workspace/components/workspace-settings-layout.vue'
import WorkspaceNav from '@/modules/workspace/components/workspace-nav.vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { RouterLinkBack, VButton, VButtonInvisible, VToggleButtonField } from '@/modules/shared/components'

const workspaceSettingsStore = useWorkspaceSettingsStore()

const selectedTab = computed(() => 'company_settings' as const)

const form = ref({
  funding_application_enabled: false,
  deal_pipeline_enabled: false,
  secondary_deal_pipeline_enabled: false,
  accepts_shared_deals: false,
  company_index_enabled: false,
  company_discussions_enabled: false,
  sort_discussion_newest_to_oldest: true,
  all_investors_view_captable: false,
})

const setForm = () => {
  const settings = workspaceSettingsStore.settings
  if (settings) {
    form.value = {
      funding_application_enabled: settings.funding_application_enabled ?? false,
      deal_pipeline_enabled: settings.deal_pipeline_enabled ?? false,
      secondary_deal_pipeline_enabled: settings.secondary_deal_pipeline_enabled ?? false,
      accepts_shared_deals: settings.accepts_shared_deals ?? false,
      company_index_enabled: settings.company_index_enabled ?? false,
      company_discussions_enabled: settings.company_discussions_enabled ?? false,
      sort_discussion_newest_to_oldest: settings.sort_discussion_newest_to_oldest ?? true,
      all_investors_view_captable: settings.all_investors_view_captable ?? false,
    }
  }
}

watch(() => workspaceSettingsStore.settings, setForm, { immediate: true })

const submit = async () => {
  await workspaceSettingsStore.updateSettings(form.value)
}

onMounted(() => {
  workspaceSettingsStore.fetch()
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6">
      <RouterLinkBack class="text-lg" :to="{ name: 'dashboard' }">
        <VButtonInvisible>← Back</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <WorkspaceNav />

    <WorkspaceSettingsLayout :selected-tab="selectedTab">
      <form @submit.prevent="submit" class="space-y-6">
        <div class="text-lg font-semibold">Company Settings</div>

        <VToggleButtonField
          v-model="form.funding_application_enabled"
          label="Public company registration"
          description="Allow companies to register through a public URL. You can attach a custom application, or companies can share deal information that populates the Review Pending folder in your deal pipeline."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.deal_pipeline_enabled"
          label="Deal pipeline visibility"
          description="Allow investors to view the deal pipeline. When enabled, group admins can grant investors access to specific folders to review company profiles."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.secondary_deal_pipeline_enabled"
          label="Segment deal pipeline"
          description="Divide the deal pipeline into two sections: one visible to admins and investors, and another visible only to group admins."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.accepts_shared_deals"
          label="Shared deals"
          description="Allow other Venture360 groups to share deals with your organization. Shared deals will appear in the Shared folder of your deal pipeline."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.company_index_enabled"
          label="Company list visibility"
          description="Allow investors to view the list of companies and company profiles associated with your group."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.company_discussions_enabled"
          label="Company discussions"
          description="Enable discussion threads on company profiles where investors and company users can communicate."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.sort_discussion_newest_to_oldest"
          label="Discussion sort order"
          description="Set the default sort order for discussions across the application."
          trueLabel="Newest to Oldest"
          falseLabel="Oldest to Newest"
        />

        <!-- Cap Table Section (only for entrepreneur groups) -->
        <template v-if="!workspaceSettingsStore.workspace?.is_investment_group">
          <div class="my-8 h-px bg-gray-100"></div>

          <div class="text-lg font-semibold">Cap Table</div>

          <VToggleButtonField
            v-model="form.all_investors_view_captable"
            label="Cap table visibility"
            description="Allow all investors who have invested in your company to view the full cap table, regardless of their group."
            trueLabel="Enable"
            falseLabel="Disable"
          />
        </template>

        <div class="pt-4">
          <VButton variant="v-blue" size="lg" type="submit" :loading="workspaceSettingsStore.loading">
            Save Changes
          </VButton>
        </div>
      </form>
    </WorkspaceSettingsLayout>
  </TheLayout>
</template>
